import { getAuthHeaders } from '../utils/authUtils';
import axios from 'axios';

class EditTrackingService {
    constructor() {
        this.currentSession = null;
        this.edits = [];
        this.isTracking = false;
        this.lastActivityTime = Date.now();
        this.continuousTrackingInterval = null; // Add this to track continuous time
    }

    async startSession(videoId, language, userLevel) {
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            
            if (!authUser || !authToken) {
                console.error("User not authenticated");
                return null;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/start`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    userId: authUser.id || authUser._id,
                    email: authUser.email,
                    videoId,
                    language,
                    userLevel
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to start edit session');
            }
            
            this.currentSession = await response.json();
            this.isTracking = true;
            this.edits = [];
            this.lastActivityTime = Date.now();
            
            // Set up continuous tracking with a heartbeat
            if (this.continuousTrackingInterval) {
                clearInterval(this.continuousTrackingInterval);
            }
            
            // Send a heartbeat every 30 seconds to keep the session alive and record time
            this.continuousTrackingInterval = setInterval(() => {
                // Track a minimal edit to register session activity
                // This ensures we track time even when not actively editing
                this.sendHeartbeat();
            }, 30000); // 30-second interval
            
            // Set up activity tracking
            this.setupActivityTracking();
            
            console.log('Edit session started:', this.currentSession);
            return this.currentSession;
        } catch (error) {
            console.error('Error starting edit session:', error);
            return null;
        }
    }
    
    setupActivityTracking() {
        // Listen for user activity
        const events = ['mousedown', 'keydown', 'mousemove', 'wheel', 'touchstart', 'click'];
        
        events.forEach(event => {
            document.addEventListener(event, () => {
                this.lastActivityTime = Date.now();
            });
        });
    }

    async endSession() {
        if (!this.currentSession || !this.isTracking) {
            console.warn('No active edit session to end');
            return null;
        }
        
        // Clear the continuous tracking interval
        if (this.continuousTrackingInterval) {
            clearInterval(this.continuousTrackingInterval);
            this.continuousTrackingInterval = null;
        }
        
        try {
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${this.currentSession.id}/end`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(true)
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to end edit session');
            }
            
            const endedSession = await response.json();
            this.isTracking = false;
            this.currentSession = null;
            
            console.log('Edit session ended:', endedSession);
            return endedSession;
        } catch (error) {
            console.error('Error ending edit session:', error);
            return null;
        }
    }

    async trackEdit(index, editType, field, oldValue, newValue) {
        if (!this.currentSession || !this.isTracking) {
            console.warn('No active edit session for tracking edits');
            return;
        }
        
        try {
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            
            // Store edit locally
            this.edits.push({
                index,
                editType,
                field,
                oldValue,
                newValue,
                timestamp: new Date().toISOString()
            });

            // Create the request body
            const requestBody = {
                index,
                editType,
                field,
                oldValue,
                newValue
            };
            
            // Send to server
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${this.currentSession.id}/edits`;
            const headers = getAuthHeaders(true);
            headers['Content-Type'] = 'application/json';
            const response = await axios.post(url, requestBody, {headers});
        } catch (error) {
            console.error('Error tracking edit:', error);
        }
    }

    async getAvailableFolders() {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/folders`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            
            if (!response.ok) {
                throw new Error('Failed to fetch available folders');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching available folders:', error);
            return [];
        }
    }

    async getUserStats() {
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const userId = authUser.email;
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/${userId}/stats`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            
            if (!response) {
                throw new Error('Failed to fetch user stats');
            }
            
            return response.data;
        } catch (error) {
            console.error('Error fetching user stats:', error);
            return null;
        }
    }

    async clearTrackingData(){
        try{
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/clear-all-data`;
            const headers = getAuthHeaders(true);
            const response = await axios.delete(url, {headers});
            
            if (!response) {
                throw new Error('Failed to clear all data');
            }

        }catch(error){
            console.error('Error clearing data', error);
            return null;
        }
    }

    

    async getGlobalLeaderboard(sortBy = 'time', folderPath = null) {
        try {
            console.log('getGlobalLeaderboard called with folderPath:', folderPath);
            
            let url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/leaderboard/global?sortBy=${sortBy}`;
            
            // Add folder filter if provided
            if (folderPath && folderPath !== '/') {
              url += `&folderPath=${encodeURIComponent(folderPath)}`;
            }
            
            console.log('Making request to:', url);
            
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            
            return response.data;
        } catch (error) {
            console.error('Error fetching global leaderboard:', error);
            return [];
        }
    }

    async getVideoEditStats(videoId) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/video/${videoId}/tracking-data`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            
            if (!response) {
                throw new Error('Failed to fetch video edit stats');
            }
            
            return response.data;
        } catch (error) {
            console.error('Error fetching video edit stats:', error);
            return null;
        }
    }

    async getVideoLanguageEditStats(videoId, language) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/video/${videoId}/language/${language}/tracking-data`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            return response.data;
        } catch (error) {
            console.error('Error fetching video language edit stats:', error);
            return {};
        }
    }

    formatTime(milliseconds) {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        
        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = (minutes % 60).toString().padStart(2, '0');
        const formattedSeconds = (seconds % 60).toString().padStart(2, '0');
        
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    async sendHeartbeat() {
        if (!this.currentSession || !this.isTracking) {
            return;
        }
        
        try {
            // Create a minimal heartbeat edit to register activity without actual edits
            const heartbeatEdit = {
                type: 'heartbeat',
                timestamp: new Date().toISOString()
            };
            
            // Keep track of the heartbeat locally
            this.edits.push(heartbeatEdit);

            // Send the heartbeat to the server
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${this.currentSession.id}/heartbeat`;
            const headers = getAuthHeaders(true);
            await axios.post(url, {}, {headers});
            
            console.log('Heartbeat sent at:', new Date().toISOString());
        } catch (error) {
            console.error('Error sending heartbeat:', error);
        }
    }

    async getUserVideoBreakdown() {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/current/videos`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            return response.data;
        } catch (error) {
            console.error('Error fetching user video breakdown:', error);
            return [];
        }
    }

    async getUserLanguageBreakdown() {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/current/languages`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            return response.data;
        } catch (error) {
            console.error('Error fetching user language breakdown:', error);
            return [];
        }
    }

    async getUserDetailedStats(userId, folderPath) {
        try {
            let url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/${userId}/detailed`;
            
            if (folderPath && folderPath !== '/') {
                url += `?folderPath=${encodeURIComponent(folderPath)}`;
            }
            
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            return response.data;
        } catch (error) {
            console.error('Error fetching user detailed stats:', error);
            return null;
        }
    }

    async getUserVideoLanguageStats(userId, videoId) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/${userId}/videos/${videoId}/languages`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            return response.data;
        } catch (error) {
            console.error('Error fetching user video language stats:', error);
            return null;
        }
    }

    async getVideoContributors(videoId) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/videos/${videoId}/contributors`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, {headers});
            return response.data;
        } catch (error) {
            console.error('Error fetching video contributors:', error);
            return [];
        }
    }
}

export default new EditTrackingService();